import React from 'react';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { Container, Link, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import lostConnectionIcon from '@/resources/map_style/lost_connection_map_marker.png';
import { StringKeys } from '@/resources/strings/StringKeys';
import { formatTimeAgo, formatTimeHour } from '@/utils/time/formatTime';
import { useLocationSharingActions } from '@/stores/map/locationSharingStore';
import { useDashboardActions } from '@/stores/map/dashboardStore';
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  InfoWindow,
} from '@vis.gl/react-google-maps';

/**
 * @typedef {Object} ConnectionLossMarkerProps
 * @property {{ lat: number, lng: number }} position - The position of the marker.
 * @property {string} userAuthId - The unique identifier for the user.
 * @property {number} lastUpdated - The timestamp of the last update in milliseconds.
 * @property {boolean} isSelected - Whether the marker is selected.
 * @property {string} groupName - The name of the group the user belongs to.
 */

/**
 * ConnectionLossMarker component displays a marker for users who have lost connection.
 * @param {ConnectionLossMarkerProps} props
 */
function ConnectionLossMarker({
  position,
  userAuthId,
  lastUpdated,
  isSelected,
  groupName,
}) {
  const { groups } = useOrganizationUpdaterProvider();
  const locationSharingActions = useLocationSharingActions();
  const dashboardActions = useDashboardActions();

  const getUserInfo = () => {
    if (!groups) return null;

    const group = groups.find((group) => group.group_name === groupName);
    if (!group) return null;
    const userDetails = group.group_value?.find(
      (user) => user.auth_id === userAuthId,
    );
    if (userDetails) {
      const info = {
        groupName: group.group_name,
        firstName: userDetails.first_name,
        lastName: userDetails.last_name,
        phoneNumber: userDetails.phone_number,
        role: userDetails.role,
        userAuthId: userAuthId,
      };
      return info;
    }
    return null;
  };

  const userInfo = getUserInfo();

  if (!position || !position.lat || !position.lng) {
    console.warn(
      `Invalid position for user ${userAuthId}: ${JSON.stringify(position)}`,
    );
    return null;
  }

  return (
    <>
      <AdvancedMarker
        position={position}
        onClick={() => {
          locationSharingActions.setSelectedLocationMarkerByUserId(userAuthId);
          // Also open the dashboard
          dashboardActions.openDashboard(userAuthId);
        }}
        style={{ cursor: 'pointer' }}
        anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
      >
        <img src={lostConnectionIcon} alt="" />
      </AdvancedMarker>
      {isSelected && (
        <InfoWindow
          position={position}
          onCloseClick={() => {
            locationSharingActions.setSelectedLocationMarkerByUserId(null);
            dashboardActions.closeDashboard();
          }}
          pixelOffset={[0, -40]}
        >
          <Stack sx={{ textAlign: 'center' }} spacing={1}>
            <Typography style={{ fontWeight: 'bold' }}>
              {t(StringKeys.NAME_LOST_CONNECTION, {
                name: `${userInfo?.firstName} ${userInfo?.lastName}`,
              })}
            </Typography>
            <Stack spacing={1}>
              <Container>
                <Typography>
                  <Link href={`tel:${userInfo?.phoneNumber}`}>
                    {userInfo?.phoneNumber}{' '}
                  </Link>
                </Typography>
                <Typography>
                  {t(StringKeys.GROUP)}: {userInfo?.groupName}
                </Typography>
              </Container>

              <Container>
                <Typography>
                  <Link
                    href={`https://maps.google.com/?q=${position.lat},${position.lng}`}
                    target="_blank"
                  >
                    {t(StringKeys.LAST_KNOWN_LOCATION)}
                  </Link>
                </Typography>
                <Typography>
                  {t(StringKeys.LAST_SEEN_AT)} {formatTimeHour(lastUpdated)},
                  {<br />}
                  {formatTimeAgo(lastUpdated)}
                </Typography>
              </Container>
            </Stack>
          </Stack>
        </InfoWindow>
      )}
    </>
  );
}

ConnectionLossMarker.displayName = 'ConnectionLossMarker';

export default ConnectionLossMarker;
